import styled, { CSSObject } from '@emotion/styled'
import { margin, MarginProps } from 'styled-system'
import { defaultTheme, DefaultTheme } from 'assets/theme/theme'

interface DividerProps extends MarginProps {
  variant?: 'dashed' | 'solid'
  position?: 'horizontal' | 'vertical'
  color?: keyof typeof defaultTheme.colors.border | 'currentColor'
}

type StyleComponentProps = Required<
  Pick<DividerProps, 'variant' | 'position' | 'color'>
>

const generateStyle = ({
  theme,
  variant,
  color,
  position
}: StyleComponentProps & { theme: DefaultTheme }) => {
  const horizontalSettings = {
    width: '100%'
  }

  const verticalSettings = {
    height: 'auto',
    alignSelf: 'normal'
  }

  const borderColor =
    color === 'currentColor' ? 'currentColor' : theme.colors.border[color]

  /**
   * Use the parameter's name combination to create keys for each possible variation
   * and simplify if statements.
   *  */
  const style: { [key: string]: CSSObject } = {
    dashedhorizontal: {
      height: 1,
      ...horizontalSettings,
      ...theme.mixins.dashedLine('top', color)
    },
    dashedvertical: {
      width: 1,
      ...verticalSettings,
      ...theme.mixins.dashedLine('right', color)
    },
    solidhorizontal: {
      borderTop: '1px solid ' + borderColor,
      ...horizontalSettings
    },
    solidvertical: {
      borderRight: '1px solid ' + borderColor,
      ...verticalSettings
    }
  }

  return {
    ...style[variant + position],
    color: borderColor,
    backgroundColor: 'transparent'
  }
}

/**
 * The <hr> HTML element represents a thematic break between contents: for example, a
 * change of information in a card element. It is defined in semantic terms rather than
 * styling purposes, if you wish to draw a line, you should do so using appropriate CSS.
 */
const Divider = ({
  variant = 'dashed',
  position = 'horizontal',
  color = 'primary',
  ...props
}: DividerProps) => (
  <StyledDivider
    variant={variant}
    position={position}
    color={color}
    {...props}
  />
)

export default Divider

const StyledDivider = styled.hr<StyleComponentProps>(
  () => ({
    border: 'none'
  }),
  props => generateStyle(props),
  margin
)
